// File: frontend/src/components/Activities/ActivityForm.jsx
// Activity form component for creating and editing activities

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  UserIcon,
  BuildingOfficeIcon,
  ClockIcon,
  TagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MicrophoneIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import useActivityVoiceFill from '../../hooks/useActivityVoiceFill';

// ids are UUIDs (strings) in this CRM — compare them as strings, never parseInt().
// Tolerant of legacy numeric ids too (coerces both sides to string).
const idEq = (a, b) => a != null && b != null && String(a) === String(b);

const ActivityForm = ({
  isOpen,
  onClose,
  activity = null,
  contactId = null,
  companyId = null,
  leadId = null,
  opportunityId = null,
  initialType = null,
  onSuccess
}) => {
  const isEditing = !!activity;
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    type: '',
    subject: '',
    description: '',
    contact_id: '',
    company_id: '',
    lead_id: '',
    activity_date: '',
    due_date: '',
    duration_minutes: '',
    priority: 'medium',
    completed: false,
    outcome: '',
    tags: ''
  });

  const [errors, setErrors] = useState({});

  const voice = useActivityVoiceFill();

  const handleDitar = async () => {
    try {
      if (!voice.recording) { await voice.start(); return; }
      const sel = contacts.find((c) => idEq(c.id, formData.contact_id));
      const ctx = sel ? `Contato: ${sel.first_name} ${sel.last_name || ''}${sel.company_name ? `, empresa ${sel.company_name}` : ''}` : '';
      const filled = await voice.stopAndFill(ctx);
      if (!filled) return;
      setFormData((prev) => ({
        ...prev,
        type: filled.type || prev.type,
        subject: filled.subject || prev.subject,
        description: filled.description || prev.description,
        outcome: filled.outcome || prev.outcome,
        tags: Array.isArray(filled.tags) && filled.tags.length ? filled.tags.join(', ') : prev.tags,
        priority: filled.priority || prev.priority,
      }));
      toast.success('Campos preenchidos por voz — revise e salve');
    } catch (e) {
      console.error('Ditar atividade falhou:', e);
      toast.error(e.response?.data?.message || 'Falha ao estruturar a atividade');
    }
  };

  // Load existing activity data when editing
  useEffect(() => {
    console.log('🔍 ActivityForm useEffect triggered:', { 
      activity: !!activity, 
      contactId, 
      companyId: companyId,
      companyIdType: typeof companyId,
      leadId,
      isOpen 
    });
    
    if (activity) {
      setFormData({
        type: activity.type || '',
        subject: activity.subject || '',
        description: activity.description || '',
        contact_id: activity.contact_id || '',
        company_id: activity.company_id || '',
        lead_id: activity.lead_id || '',
        activity_date: activity.activity_date ? 
          new Date(activity.activity_date).toISOString().slice(0, 16) : '',
        due_date: activity.due_date ? 
          new Date(activity.due_date).toISOString().slice(0, 16) : '',
        duration_minutes: activity.duration_minutes || '',
        priority: activity.priority || 'medium',
        completed: activity.completed || false,
        outcome: activity.outcome || '',
        tags: activity.tags ? activity.tags.join(', ') : ''
      });
    } else {
      // Pre-populate with passed props - this is critical for company context
      const newFormData = {
        ...formData,
        type: initialType || '',
        contact_id: contactId || '',
        company_id: companyId ? String(companyId) : '',
        lead_id: leadId || '',
        activity_date: new Date().toISOString().slice(0, 16)
      };

      console.log('🎯 ActivityForm setting new formData:', newFormData);
      setFormData(newFormData);
    }
  }, [activity, contactId, companyId, leadId, opportunityId, initialType, isOpen]);

  // Fetch contacts for dropdown - with intelligent filtering and search
  const { data: contacts = [] } = useQuery(
    ['contacts-for-activity-form', companyId, contactId],
    async () => {
      let url = '/contacts?limit=200'; // Increased limit for better search
      const params = new URLSearchParams();
      
      // If we're in a company context, prioritize company contacts
      if (companyId) {
        params.append('company_id', companyId);
        params.append('include_unassigned', 'true'); // Also show unassigned contacts
      }
      
      // Always include active contacts only
      params.append('active_only', 'true');
      
      if (params.toString()) {
        url += '&' + params.toString();
      }
      
      const response = await api.get(url);
      let contactsList = response.data.contacts || [];
      
      // If we have a specific contact context, ensure it's at the top
      if (contactId) {
        const specificContact = contactsList.find(c => idEq(c.id, contactId));
        if (specificContact) {
          contactsList = [specificContact, ...contactsList.filter(c => !idEq(c.id, contactId))];
        }
      }
      
      // Enhanced sorting by relevance
      contactsList.sort((a, b) => {
        // 1. Pre-selected contact first
        if (contactId) {
          if (idEq(a.id, contactId)) return -1;
          if (idEq(b.id, contactId)) return 1;
        }

        // 2. Prioritize contacts from the same company
        if (companyId) {
          const aIsFromCompany = idEq(a.company_id, companyId);
          const bIsFromCompany = idEq(b.company_id, companyId);
          if (aIsFromCompany && !bIsFromCompany) return -1;
          if (bIsFromCompany && !aIsFromCompany) return 1;
        }
        
        // 3. Contacts with companies before unassigned
        if (a.company_id && !b.company_id) return -1;
        if (b.company_id && !a.company_id) return 1;
        
        // 4. Then by recent activity/updates
        return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      });
      
      return contactsList;
    },
    { enabled: isOpen }
  );

  // Fetch companies for dropdown
  const { data: companies = [] } = useQuery(
    'companies-for-activity-form',
    async () => {
      const response = await api.get('/companies?limit=100');
      const companiesList = response.data.companies || [];
      
      // If we have a specific company, ensure it's at the top
      if (companyId) {
        const specificCompany = companiesList.find(c => idEq(c.id, companyId));
        if (specificCompany) {
          const otherCompanies = companiesList.filter(c => !idEq(c.id, companyId));
          return [specificCompany, ...otherCompanies];
        }
      }
      
      return companiesList;
    },
    { enabled: isOpen }
  );

  // Fetch leads for dropdown when needed
  const { data: leads = [] } = useQuery(
    'leads-for-activity-form',
    async () => {
      const response = await api.get('/leads?limit=100&status=active');
      return response.data.leads || [];
    },
    { enabled: isOpen && !contactId }
  );

  // Auto-populate company when contact is selected with notification
  useEffect(() => {
    if (formData.contact_id && contacts.length > 0) {
      const selectedContact = contacts.find(c => idEq(c.id, formData.contact_id));
      if (selectedContact && selectedContact.company_id && !formData.company_id) {
        const company = companies.find(c => c.id === selectedContact.company_id);
        setFormData(prev => ({
          ...prev,
          company_id: selectedContact.company_id
        }));
        
        // Show success notification for auto-complete
        if (company) {
          toast.success(`Empresa "${company.name}" selecionada automaticamente!`, {
            duration: 3000,
            icon: '🏢'
          });
        }
      }
    }
  }, [formData.contact_id, contacts, formData.company_id, companies]);

  // Dedicated useEffect for company pre-selection when companyId is provided
  useEffect(() => {
    console.log('🏢 Company pre-selection effect triggered:', {
      companyId,
      companyIdType: typeof companyId,
      companiesCount: companies.length,
      currentCompanyId: formData.company_id,
      isOpen,
      hasActivity: !!activity,
      condition: companyId && companies.length > 0 && isOpen && !activity
    });
    
    if (companyId && companies.length > 0 && isOpen && !activity) {
      console.log('🏢 Company pre-selection condition met, searching for company...');
      
      // Find the target company - direct UUID string comparison
      const targetCompany = companies.find(c => c.id === companyId);
      console.log('🏢 Target company search:', {
        searchingFor: companyId,
        targetCompany,
        allCompanies: companies.map(c => ({ id: c.id, name: c.name }))
      });
      
      if (targetCompany && formData.company_id !== companyId) {
        console.log('🏢 Setting company_id to:', companyId);
        setFormData(prev => ({
          ...prev,
          company_id: companyId
        }));
      }
    }
  }, [companyId, companies, isOpen, activity, formData.company_id]);

  // Helper function to check if company should be disabled
  const isCompanyDisabled = () => {
    console.log('🔧 isCompanyDisabled check:', {
      companyId,
      'formData.company_id': formData.company_id,
      'formData.contact_id': formData.contact_id,
      'contacts.length': contacts.length
    });
    
    // If companyId prop is passed (from company context), always disable
    if (companyId) {
      console.log('🔧 Company disabled due to companyId prop:', companyId);
      return true;
    }
    
    // If contact is selected and has a company, disable
    if (formData.contact_id && contacts.length > 0) {
      const selectedContact = contacts.find(c => idEq(c.id, formData.contact_id));
      const result = selectedContact && selectedContact.company_id;
      console.log('🔧 Company disabled due to contact:', { selectedContact, result });
      return result;
    }
    
    console.log('🔧 Company NOT disabled');
    return false;
  };

  // Create/Update mutation
  const mutation = useMutation(
    async (data) => {
      if (isEditing) {
        return await api.put(`/activities/${activity.id}`, data);
      } else {
        return await api.post('/activities', data);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEditing ? 'Atividade atualizada com sucesso!' : 'Atividade criada com sucesso!');
        queryClient.invalidateQueries('activities');
        queryClient.invalidateQueries('contact-activities');
        queryClient.invalidateQueries('company-activities');
        if (onSuccess) onSuccess();
        onClose();
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.message || 'Erro ao salvar atividade';
        toast.error(errorMsg);
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      }
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.type.trim()) {
      newErrors.type = 'Tipo de atividade é obrigatório';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Assunto é obrigatório';
    }
    
    // At least one relationship required
    if (!formData.contact_id && !formData.company_id && !formData.lead_id) {
      newErrors.contact_id = 'Contato, empresa ou lead é obrigatório';
      newErrors.company_id = 'Contato, empresa ou lead é obrigatório';
    }

    // Date validations
    if (formData.activity_date) {
      const activityDate = new Date(formData.activity_date);
      const futureLimit = new Date();
      futureLimit.setFullYear(futureLimit.getFullYear() + 1);
      
      if (activityDate > futureLimit) {
        newErrors.activity_date = 'Data não pode ser muito no futuro';
      }
      
      if (formData.due_date) {
        const dueDate = new Date(formData.due_date);
        if (dueDate < activityDate) {
          newErrors.due_date = 'Data de prazo deve ser posterior à data da atividade';
        }
      }
    }

    // Duration validation
    if (formData.duration_minutes && 
        (isNaN(formData.duration_minutes) || parseInt(formData.duration_minutes) <= 0)) {
      newErrors.duration_minutes = 'Duração deve ser um número positivo';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare data for API
    const submitData = {
      ...formData,
      contact_id: formData.contact_id || null,
      company_id: formData.company_id || null,
      lead_id: formData.lead_id || null,
      opportunity_id: opportunityId || null,
      duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
      tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
      activity_date: formData.activity_date || null,
      due_date: formData.due_date || null
    };

    mutation.mutate(submitData);
  };

  const activityTypes = [
    { value: 'call', label: 'Ligação', icon: PhoneIcon },
    { value: 'email', label: 'Email', icon: EnvelopeIcon },
    { value: 'meeting', label: 'Reunião', icon: CalendarIcon },
    { value: 'note', label: 'Anotação', icon: ChatBubbleLeftRightIcon },
    { value: 'task', label: 'Tarefa', icon: ClipboardDocumentListIcon },
    { value: 'whatsapp', label: 'WhatsApp', icon: DevicePhoneMobileIcon },
    { value: 'linkedin', label: 'LinkedIn', icon: UserGroupIcon }
  ];

  const priorities = [
    { value: 'low', label: 'Baixa', color: 'text-green-600' },
    { value: 'medium', label: 'Média', color: 'text-yellow-600' },
    { value: 'high', label: 'Alta', color: 'text-red-600' }
  ];

  const getTypeIcon = (type) => {
    const activityType = activityTypes.find(t => t.value === type);
    return activityType ? activityType.icon : ChatBubbleLeftRightIcon;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {formData.type ? (
              React.createElement(getTypeIcon(formData.type), {
                className: "h-6 w-6 text-blue-600"
              })
            ) : (
              <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />
            )}
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Editar Atividade' : 'Nova Atividade'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Context Indicator */}
        {(contactId || companyId || leadId) && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center space-x-2">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              </div>
              <div className="text-sm text-blue-800">
                <span className="font-medium">Contexto:</span>{' '}
                {contactId && contacts.length > 0 && (
                  <span>
                    Contato{' '}
                    <strong>
                      {contacts.find(c => idEq(c.id, contactId))?.first_name}{' '}
                      {contacts.find(c => idEq(c.id, contactId))?.last_name}
                    </strong>
                  </span>
                )}
                {companyId && companies.length > 0 && (
                  <span>
                    {contactId && ' • '}
                    Empresa{' '}
                    <strong>
                      {companies.find(c => idEq(c.id, companyId))?.name}
                    </strong>
                  </span>
                )}
                {leadId && leads.length > 0 && (
                  <span>
                    Lead{' '}
                    <strong>
                      {leads.find(l => idEq(l.id, leadId))?.full_name ||
                        `${leads.find(l => idEq(l.id, leadId))?.first_name} ${leads.find(l => idEq(l.id, leadId))?.last_name}`}
                    </strong>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={handleDitar}
              disabled={voice.busy}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-white ${voice.recording ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-800'} disabled:opacity-50`}
              title="Ditar atividade por voz"
            >
              {voice.recording ? <StopIcon className="h-4 w-4" /> : <MicrophoneIcon className="h-4 w-4" />}
              {voice.busy ? 'Estruturando…' : voice.recording ? 'Parar' : 'Ditar atividade'}
            </button>
          </div>
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informações Básicas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Atividade *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.type ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecionar tipo</option>
                    {activityTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prioridade
                  </label>
                  <div className="relative">
                    <ExclamationTriangleIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {priorities.map(priority => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assunto *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.subject ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Assunto da atividade"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Detalhes da atividade..."
                  />
                </div>
              </div>
            </div>

            {/* Relationships */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Relacionamentos
                {(contactId || companyId || leadId) && (
                  <span className="text-sm font-normal text-blue-600 ml-2">
                    (contexto pré-selecionado)
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contato
                    {contactId && (
                      <span className="text-xs text-blue-600 ml-1">(pré-selecionado)</span>
                    )}
                    {!contactId && !formData.contact_id && (
                      <span className="text-xs text-green-600 ml-1">💡 Auto-completa empresa</span>
                    )}
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="contact_id"
                      value={formData.contact_id}
                      onChange={handleChange}
                      disabled={!!contactId}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.contact_id ? 'border-red-300' : 'border-gray-300'
                      } ${contactId ? 'bg-blue-50 border-blue-200' : ''}`}
                    >
                      <option value="">Selecionar contato</option>
                      {contacts.map(contact => {
                        const contactCompany = companies.find(c => c.id === contact.company_id);
                        return (
                          <option key={contact.id} value={contact.id}>
                            {contact.first_name} {contact.last_name}
                            {contactCompany && ` • ${contactCompany.name}`}
                            {idEq(contact.company_id, companyId) && ' ✓'}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {errors.contact_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.contact_id}</p>
                  )}
                  {formData.contact_id && contacts.length > 0 && (() => {
                    const selectedContact = contacts.find(c => idEq(c.id, formData.contact_id));
                    const contactCompany = selectedContact && companies.find(c => c.id === selectedContact.company_id);
                    return selectedContact && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-center space-x-2 text-sm">
                          <UserIcon className="h-4 w-4 text-blue-600" />
                          <span className="font-medium text-blue-900">
                            {selectedContact.first_name} {selectedContact.last_name}
                          </span>
                          {contactCompany && (
                            <>
                              <span className="text-blue-600">•</span>
                              <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                              <span className="text-blue-700">{contactCompany.name}</span>
                            </>
                          )}
                        </div>
                        {selectedContact.email && (
                          <div className="mt-1 text-xs text-blue-600">
                            📧 {selectedContact.email}
                          </div>
                        )}
                        {selectedContact.phone && (
                          <div className="mt-1 text-xs text-blue-600">
                            📱 {selectedContact.phone}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa
                    {isCompanyDisabled() && (
                      <span className="text-xs text-blue-600 ml-1">(auto-selecionada)</span>
                    )}
                  </label>
                  <div className="relative">
                    <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="company_id"
                      value={formData.company_id}
                      onChange={handleChange}
                      disabled={isCompanyDisabled()}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.company_id ? 'border-red-300' : 'border-gray-300'
                      } ${isCompanyDisabled() ? 'bg-blue-50 border-blue-200 cursor-not-allowed' : ''}`}
                    >
                      <option value="">Selecionar empresa</option>
                      {companies.map(company => {
                        console.log('🏢 Company option:', { 
                          id: company.id, 
                          name: company.name,
                          'companyId': companyId,
                          'formData.company_id': formData.company_id,
                          'match': company.id == formData.company_id
                        });
                        return (
                          <option key={company.id} value={company.id}>
                            {company.name}
                            {idEq(company.id, companyId) && ' ✓'}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {errors.company_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.company_id}</p>
                  )}
                  {formData.company_id && companies.length > 0 && (() => {
                    const selectedCompany = companies.find(c => idEq(c.id, formData.company_id));
                    const companyContacts = contacts.filter(c => idEq(c.company_id, formData.company_id));
                    
                    return selectedCompany && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center space-x-2 text-sm">
                          <BuildingOfficeIcon className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-900">
                            {selectedCompany.name}
                          </span>
                          {isCompanyDisabled() && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              🔒 bloqueada
                            </span>
                          )}
                          {companyContacts.length > 0 && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                              {companyContacts.length} contato{companyContacts.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {selectedCompany.industry && (
                          <div className="mt-1 text-xs text-green-600">
                            🏢 {selectedCompany.industry}
                          </div>
                        )}
                        {isCompanyDisabled() && (
                          <div className="mt-1 text-xs text-blue-600">
                            💡 Empresa bloqueada porque o contato pertence a ela
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                
                {/* Lead selector - show only when not in contact context */}
                {!contactId && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lead (opcional se já selecionou contato)
                      {leadId && (
                        <span className="text-xs text-blue-600 ml-1">(pré-selecionado)</span>
                      )}
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <select
                        name="lead_id"
                        value={formData.lead_id}
                        onChange={handleChange}
                        disabled={!!leadId}
                        className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          leadId ? 'bg-blue-50 border-blue-200' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Selecionar lead</option>
                        {leads.map(lead => (
                          <option key={lead.id} value={lead.id}>
                            {lead.full_name || `${lead.first_name} ${lead.last_name}`}
                            {idEq(lead.id, leadId) && ' ✓'}
                          </option>
                        ))}
                      </select>
                    </div>
                    {leadId && (
                      <p className="mt-1 text-xs text-blue-600">
                        💡 Esta atividade será vinculada ao lead especificado
                      </p>
                    )}
                  </div>
                )}
              </div>
              {(errors.contact_id || errors.company_id) && (
                <p className="mt-2 text-sm text-amber-600">
                  * Pelo menos um contato, empresa ou lead deve ser selecionado
                </p>
              )}
            </div>

            {/* Scheduling */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Agendamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data da Atividade
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="datetime-local"
                      name="activity_date"
                      value={formData.activity_date}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.activity_date ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.activity_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.activity_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Prazo
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="datetime-local"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.due_date ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.due_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.due_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duração (minutos)
                  </label>
                  <div className="relative">
                    <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="number"
                      name="duration_minutes"
                      value={formData.duration_minutes}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.duration_minutes ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="30"
                      min="1"
                    />
                  </div>
                  {errors.duration_minutes && (
                    <p className="mt-1 text-sm text-red-600">{errors.duration_minutes}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Status and Results */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Status e Resultado</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="completed"
                    checked={formData.completed}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                    Atividade concluída
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Resultado
                    </label>
                    <textarea
                      name="outcome"
                      value={formData.outcome}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Resultado ou próximos passos..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags
                    </label>
                    <div className="relative">
                      <TagIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <textarea
                        name="tags"
                        value={formData.tags}
                        onChange={handleChange}
                        rows="3"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="urgente, follow-up, proposta (separar por vírgula)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isLoading 
                ? (isEditing ? 'Atualizando...' : 'Criando...') 
                : (isEditing ? 'Atualizar Atividade' : 'Criar Atividade')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivityForm;